import React, { useState, useEffect } from "react";
import { Form, Row, Col } from "react-bootstrap";
import dayjs from "dayjs";
import Buttons from "../buttons/Buttons";

const DateRangePicker = ({ onApply, myData }) => {
  // Determine initial selected option based on incoming dates
  const getInitialOption = (data) => {
    if (!data?.fromDate || !data?.toDate) return "";
    const today = dayjs();
    const from = dayjs(data.fromDate, "DD/MM/YYYY");
    const diff = today.diff(from, "day");

    switch (diff) {
      case 7: return "Last 7 days";
      case 10: return "Last 10 days";
      case 15: return "Last 15 days";
      case 30: return "Last 30 days";
      default: return "Custom";
    }
  };

  const [selectedOption, setSelectedOption] = useState(getInitialOption(myData));
  const [fromDate, setFromDate] = useState(
    myData?.fromDate ? dayjs(myData.fromDate, "DD/MM/YYYY").format("YYYY-MM-DD") : ""
  );
  const [toDate, setToDate] = useState(
    myData?.toDate ? dayjs(myData.toDate, "DD/MM/YYYY").format("YYYY-MM-DD") : ""
  );

  // Sync state when parent data changes
  useEffect(() => {
    if (myData) {
      setSelectedOption(getInitialOption(myData));
      setFromDate(myData.fromDate ? dayjs(myData.fromDate, "DD/MM/YYYY").format("YYYY-MM-DD") : "");
      setToDate(myData.toDate ? dayjs(myData.toDate, "DD/MM/YYYY").format("YYYY-MM-DD") : "");
    } else {
      setSelectedOption("");
      setFromDate("");
      setToDate("");
    }
  }, [myData]);

  const calculateDateRange = (option) => {
    const today = dayjs();
    let from = today;

    switch (option) {
      case "Last 7 days": from = today.subtract(7, "day"); break;
      case "Last 10 days": from = today.subtract(10, "day"); break;
      case "Last 15 days": from = today.subtract(15, "day"); break;
      case "Last 30 days": from = today.subtract(30, "day"); break;
      default: break;
    }

    return {
      fromDate: from.format("DD/MM/YYYY"),
      toDate: today.format("DD/MM/YYYY"),
      label: option === "Custom" ? "Custom Range" : option,
    };
  };

  const handleOptionChange = (e) => {
    const option = e.target.value;
    setSelectedOption(option);

    if (option !== "Custom") {
      const range = calculateDateRange(option);
      setFromDate(dayjs(range.fromDate, "DD/MM/YYYY").format("YYYY-MM-DD"));
      setToDate(dayjs(range.toDate, "DD/MM/YYYY").format("YYYY-MM-DD"));
    } else {
      setFromDate("");
      setToDate("");
    }
  };

  const handleDateInputChange = (e, type) => {
    const value = e.target.value;
    type === "from" ? setFromDate(value) : setToDate(value);
  };

  const handleApplyClick = () => {
    const applied = {
      fromDate: fromDate ? dayjs(fromDate).format("DD/MM/YYYY") : "",
      toDate: toDate ? dayjs(toDate).format("DD/MM/YYYY") : "",
      label: selectedOption === "Custom" ? "Custom Range" : selectedOption,
    };
    onApply?.(applied);
  };

  const handleClear = () => {
    setSelectedOption("");
    setFromDate("");
    setToDate("");
    onApply?.({ fromDate: "", toDate: "", label: "" });
  };

  // Custom styling
  const customStyles = `
    .form-check-input:checked {
      background-color: #ff8c00;
      border-color: #ff8c00;
    }
    .btn-apply-custom {
      background: linear-gradient(to bottom, #ffae42, #ff8c00);
      border-color: #ff8c00;
      color: white;
    }
    .btn-apply-custom:hover {
      background: linear-gradient(to bottom, #ff8c00, #ffae42);
    }
    .btn-apply-custom:focus, .btn-apply-custom:active {
      background: linear-gradient(to bottom, #ff8c00, #ffae42) !important;
      border-color: #ff8c00 !important;
      box-shadow: 0 0 0 0.25rem rgba(255, 140, 0, 0.25) !important;
    }
    .input-group-text { background-color: #e9ecef; border-left: none; }
    .form-control[disabled] { background-color: #e9ecef; opacity: 1; }
  `;

  return (
    <div style={{ maxWidth: "300px" }}>
      <style>{customStyles}</style>
      <Form>
        <Row className="mb-4 g-3">
          {/* {["Last 7 days", "Last 10 days", "Last 15 days", "Last 30 days", "Custom"].map((option, i) => (
            <Col xs={6} key={i}>
              <Form.Check
                type="radio"
                label={option}
                value={option}
                checked={selectedOption === option} // Derived checked state
                onChange={handleOptionChange}
              />
            </Col>
          ))} */}
          <Row className="g-2">
  {[
    "Last 7 days",
    "Last 10 days",
    "Last 15 days",
    "Last 30 days",
    "Custom",
  ].map((option, i) => {
    const id = `dateOption-${i}`;
    return (
      <Col xs={6} key={i} className="d-flex align-items-center">
        <Form.Check
          type="radio"
          id={id}
          name="dateRangeOption"
          value={option}
          checked={selectedOption === option}
          onChange={handleOptionChange}
          className="me-2"
        />
        <Form.Label
          htmlFor={id}
          style={{ cursor: "pointer", marginBottom: "0" }}
        >
          {option}
        </Form.Label>
      </Col>
    );
  })}
</Row>

        </Row>

        {selectedOption === "Custom" && (
          <Row className="mb-4 g-3 align-items-end">
            <Col className="col-6">
              <Form.Group controlId="fromDate">
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleDateInputChange(e, "from")}
                />
              </Form.Group>
            </Col>
            <Col className="col-6">
              <Form.Group controlId="toDate">
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => handleDateInputChange(e, "to")}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-end gap-2 mt-2">
          <Buttons
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClear}
            text="Clear"
          />
          <Buttons
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleApplyClick}
            text="Apply"
            disabled={!selectedOption || (selectedOption === "Custom" && (!fromDate || !toDate))}
          />
        </div>
      </Form>
    </div>
  );
};

export default DateRangePicker;
